import { z, type ZodTypeAny } from 'zod';
import { env } from '@/env';
import { tokenSync } from './native-auth';

// Cliente HTTP único. Toda chamada à API passa por aqui.
// - credentials: 'include' (cookie hub_token HttpOnly)
// - parse com zod schema antes de retornar (zero confiança no payload)
// - erro estruturado com status, code e mensagem amigável

export class ApiError extends Error {
  status: number;
  code: string | undefined;
  body: unknown;

  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

interface RequestOptions<TSchema extends ZodTypeAny | undefined> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  schema?: TSchema;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

type Parsed<T extends ZodTypeAny | undefined> = T extends ZodTypeAny ? z.infer<T> : unknown;

const errorSchema = z.object({
  error: z.string().optional(),
  message: z.string().optional(),
  code: z.string().optional(),
});

export async function api<TSchema extends ZodTypeAny | undefined = undefined>(
  path: string,
  options: RequestOptions<TSchema> = {},
): Promise<Parsed<TSchema>> {
  const { method = 'GET', body, schema, signal, headers } = options;

  const url = path.startsWith('http') ? path : `${env.VITE_API_BASE}${path}`;

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      // FormData: deixar o browser definir Content-Type com o boundary correto.
      ...(body !== undefined && !isFormData && { 'Content-Type': 'application/json' }),
      // App nativo (bundle-mode): Bearer JWT. Na web tokenSync()=null → cookie.
      ...(tokenSync() ? { Authorization: `Bearer ${tokenSync()}` } : {}),
      ...headers,
    },
    ...(body !== undefined && {
      body: isFormData ? (body as FormData) : JSON.stringify(body),
    }),
    ...(signal && { signal }),
  };

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError('Falha de rede', 0, 'NETWORK_ERROR', e);
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const parsed = errorSchema.safeParse(data);
    const msg = parsed.success
      ? (parsed.data.message ?? parsed.data.error ?? `HTTP ${res.status}`)
      : `HTTP ${res.status}`;
    const code = parsed.success ? parsed.data.code : undefined;
    throw new ApiError(msg, res.status, code, data);
  }

  if (schema) {
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      // Schema mismatch é erro de contrato — não esconder.
      console.error('[api] schema mismatch', path, parsed.error.issues);
      throw new ApiError('Resposta inválida do servidor', 500, 'SCHEMA_MISMATCH', data);
    }
    return parsed.data as Parsed<TSchema>;
  }

  return data as Parsed<TSchema>;
}
